import type { QueryClient } from '@tanstack/react-query'
import type { CommitmentActionDescriptor, CommitmentTopicItem } from './apiModels'
import type { ListStateChange } from './optimisticListState'

type Item = Partial<CommitmentTopicItem> & { id: string; review_status?: string }
type Source = { commitment_id?: string; commitment_items?: Item[] } & Partial<Item>

// Guard the changed fields against in-flight reads. Rollback preserves sibling
// edits and fresh server fields rather than restoring an entire query snapshot.
export function stageCommitmentState(qc: QueryClient, desc: CommitmentActionDescriptor): ListStateChange {
  const fields: Partial<Item> = desc.action === 'done'
    ? { status: 'done', review_status: 'kept' }
    : desc.action === 'cancel' || desc.action === 'deny' || desc.action === 'block'
    ? { status: 'canceled', ...(desc.action === 'cancel' ? {} : { review_status: 'denied' }) }
    : desc.action === 'accept'
    ? { status: 'open', snooze_until: null, review_status: 'kept' }
    : desc.action === 'keep'
    ? { review_status: 'kept' }
    : desc.action === 'setfu'
    ? { status: 'open', snooze_until: null, followup_at: desc.value, review_status: 'kept' }
    : desc.action === 'setdue'
    ? { status: 'open', due_date: desc.value, review_status: 'kept' }
    : desc.action === 'snooze'
    ? { status: 'snoozed', snooze_until: desc.value, review_status: 'kept' }
    : {}
  if (Object.keys(fields).length === 0) return { settle() {}, rollback() {} }
  const changes: ListStateChange[] = []
  const cachedSources = qc.getQueriesData<Source | Source[]>({ predicate: (query) =>
    query.queryKey[0] === 'topic-source' || query.queryKey[0] === 'topics' })
  const selectedSource = cachedSources.flatMap(([, data]) => Array.isArray(data) ? data : data ? [data] : [])
    .find((source) => source.commitment_items?.some((item) => item.id === desc.commitmentId))
  const ids = new Set(desc.action === 'setfu' && desc.scope !== 'item' && selectedSource?.commitment_items
    ? selectedSource.commitment_items.map((item) => item.id) : [desc.commitmentId])
  for (const [key] of qc.getQueriesData({ predicate: (query) =>
    query.queryKey[0] === 'topic-source' || query.queryKey[0] === 'topics' })) {
    const previous = new Map<string, Item>()
    let updating = false
    const patchItem = (item: Item, rollback: boolean): Item => {
      if (!ids.has(item.id)) return item
      if (!previous.has(item.id)) previous.set(item.id, item)
      const before = previous.get(item.id)!
      const next = { ...item }
      for (const [name, value] of Object.entries(fields)) {
        if (!rollback || Object.is(Reflect.get(item, name), value)) {
          Object.assign(next, { [name]: rollback ? Reflect.get(before, name) : value })
        }
      }
      return Object.keys(fields).some((name) => !Object.is(Reflect.get(item, name), Reflect.get(next, name))) ? next : item
    }
    const patchSource = (source: Source, rollback: boolean): Source => {
      if (!source || typeof source !== 'object') return source
      let result = source
      if (source.commitment_id && ids.has(source.commitment_id)) {
        const original = { ...source, id: source.commitment_id }
        const item = patchItem(original, rollback)
        if (item !== original) result = { ...source, ...item }
      }
      if (source.commitment_items) {
        const items = source.commitment_items.map((item) => patchItem(item, rollback))
        if (items.some((item, index) => item !== source.commitment_items?.[index])) result = { ...result, commitment_items: items }
      }
      return result
    }
    const update = (rollback = false) => {
      updating = true
      try {
        const data = qc.getQueryData<Source | Source[]>(key)
        const next = Array.isArray(data) ? data.map((source) => patchSource(source, rollback))
          : data && patchSource(data, rollback)
        const changed = Array.isArray(data) && Array.isArray(next)
          ? next.some((source, index) => source !== data[index]) : next !== data
        if (changed) qc.setQueryData(key, next)
      } finally { updating = false }
    }
    update()
    const unsubscribe = qc.getQueryCache().subscribe((event) => {
      if (!updating && event.type === 'updated' && event.action.type === 'success'
        && JSON.stringify(event.query.queryKey) === JSON.stringify(key)) update()
    })
    changes.push({ settle: unsubscribe, rollback: () => { unsubscribe(); update(true) } })
  }
  return {
    settle: () => changes.forEach((change) => change.settle()),
    rollback: () => changes.forEach((change) => change.rollback()),
  }
}
